<template>
  <div
    :class="allClasses"
    v-on="listeners"
  >
    <ERadio
      v-for="option in opts"
      :key="option.value"
      :model-value="modelValue"
      :value="option.value"
      v-bind="{ ...$attrs, name, required, size, disabled, id: null }"
      data-test-id="radios-radio"
      class="radios__radio"
      @input="onInput"
    >
      {{ option.label }}
    </ERadio>
  </div>
</template>


<script>
import classes, { getModifierClass } from '~~/common/vue/mixins/classes';
import ERadio from '~~/common/vue/components/radio';

/**
 * The radio button is a custom wrapper around the native browser
 * input, and include it's label. There is no use case for a single
 * radio button component to be used, unless you're building a new
 * radio button group component. The radio button group should
 * be always used otherwise.
 *
 * The radio button group is a component that can render a list of
 * options as radio buttons accompanied by their respective input control.
 */
export default {
  name: 'ERadios',
  components: {
    ERadio,
  },
  mixins: [classes],
  model: {
    prop: 'modelValue',
    event: 'input',
  },
  props: {
    modelValue: {
      type: String,
      default: null,
    },
    options: {
      type: Array,
      default: () => [],
      validator: (value) => value.every((item) => {
        if (typeof item === 'object') {
          return ['label', 'value'].every((property) => property in item);
        }
        return typeof item === 'string';
      }),
    },
    name: {
      type: [String, Number],
      default: '',
    },
    size: {
      type: String,
      default: 'm',
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dirty: false,
    };
  },
  computed: {
    opts() {
      return this.options.map((option) => (
        typeof option !== 'object' ?
          { label: option, value: option } :
          option
      ));
    },
    listeners() {
      const { input, ...rest } = this.$listeners;
      return rest;
    },
    allClasses() {
      const allClasses = [...this.classes];
      if (this.disabled) {
        allClasses.push(getModifierClass('radios', 'disabled'));
      }
      if (this.dirty) {
        allClasses.push(getModifierClass('radios', 'dirty'));
      }
      return allClasses;
    },
  },
  watch: {
    modelValue() {
      this.dirty = true;
    },
  },
  methods: {
    onInput(value) {
      this.$emit('input', value);
    },
  },
};
</script>

<docs>
A list of radios buttons

### States

#### Default

```js
let model;
<div>
  <ERadios
    name="test"
    v-model="m"
    required
    :options="[{value: '1', label: 'First option'}, {'value': '2', label: 'Second option'}]"
    />
  <br/>
  <div>Selected option: {{model}}</div>
</div>
```

#### Preselected

```js
let model = '2';
<div>
  <ERadios
    name="test"
    v-model="model"
    required
    :options="[{value: '1', label: 'First option'}, {'value': '2', label: 'Second option'}]"
    />
  <br/>
  <div>Selected option: {{model}}</div>
</div>
```
</docs>
